import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Megaphone,
  Tag,
  Mail,
  Calendar,
  Users,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Percent,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  Copy,
  Share2,
  BarChart3,
  Target,
  Gift,
  Lock,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  ChevronDown,
  RefreshCw,
  RefreshCcw,
  Link2,
  Bell,
  Crown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import { formatCurrency, formatDate, formatPercentage } from "../lib/utils";
import { CreateSaleEventModal } from "../components/CreateSaleEventModal";
import { SaleEventsList } from "../components/SaleEventsList";
import { SaleEvent } from "../types";
import { useOrderStatistics } from "../hooks/useOrderStatistics";
import { useDiscounts } from "../hooks/useDiscounts";
import { useEmailCampaigns } from "../hooks/useEmailCampaigns";
import { usePushNotifications } from "../hooks/usePushNotifications";
import {
  DiscountCode,
  updateDiscountCode,
  deleteDiscountCode,
} from "../services/discountService";
import { EmailCampaign } from "../services/emailCampaignService";
import { CreateDiscountCodeModal } from "../components/modals/CreateDiscountCodeModal";
import { ViewDiscountCodeModal } from "../components/modals/ViewDiscountCodeModal";
import { EditDiscountCodeModal } from "../components/modals/EditDiscountCodeModal";
import { ShareDiscountCodeModal } from "../components/modals/ShareDiscountCodeModal";
import { CreateEmailCampaignModal } from "../components/modals/CreateEmailCampaignModal";
import { ViewEmailCampaignModal } from "../components/modals/ViewEmailCampaignModal";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { CreateFreeItemRewardModal } from "../components/modals/CreateFreeItemRewardModal";
import {
  FreeItemReward,
  getAllFreeItemRewards,
  updateFreeItemReward,
  deleteFreeItemReward,
  toggleFreeItemReward,
} from "../services/rewardsService";
import {
  RushdTreat,
  getAllRushdTreats,
  createRushdTreat,
  updateRushdTreat,
  deleteRushdTreat,
  toggleRushdTreat,
} from "../services/rushdTreatService";
import {
  PushNotificationRecord,
} from "../services/pushNotificationService";
import { CreatePushNotificationModal, PushNotificationFormValues } from "../components/modals/CreatePushNotificationModal";
import { ViewPushNotificationModal } from "../components/modals/ViewPushNotificationModal";
import { Label } from "../components/ui/label";
import { CreateRushdTreatModal, RushdTreatFormData } from "../components/modals/CreateRushdTreatModal";


const dummySaleEvents = [
  {
    id: "SALE-001",
    name: "Valentine's Day Coffee Special",
    description: "Special pricing on coffee gift sets for Valentine's Day",
    type: "seasonal",
    status: "scheduled",
    discount: 25,
    startDate: new Date("2024-02-10"),
    endDate: new Date("2024-02-15"),
    products: ["Blue Mountain Coffee Gift Set", "Coffee & Spice Bundle"],
    expectedRevenue: 8500,
    actualRevenue: 0,
    createdAt: new Date("2024-01-20"),
    updatedAt: new Date("2024-01-22"),
  },
  {
    id: "SALE-002",
    name: "End of Month Clearance",
    description: "Clear out inventory with special discounts",
    type: "clearance",
    status: "active",
    discount: 40,
    startDate: new Date("2024-01-25"),
    endDate: new Date("2024-01-31"),
    products: ["Plantain Chips", "Coconut Oil"],
    expectedRevenue: 3200,
    actualRevenue: 1847.32,
    createdAt: new Date("2024-01-22"),
    updatedAt: new Date("2024-01-25"),
  },
];

const marketingTypes = [
  "All",
  "discount_codes",
  "email_campaigns",
  "sale_events",
];
const marketingStatuses = [
  "All",
  "active",
  "inactive",
  "sent",
  "draft",
  "expired",
];

export function Marketing() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState("discount_codes");
  const [rushdTreats, setRushdTreats] = useState<RushdTreat[]>([]);
  const [rushdTreatsLoading, setRushdTreatsLoading] = useState(false);
  const [rushdTreatsError, setRushdTreatsError] = useState<string | null>(null);
  const [selectedTreat, setSelectedTreat] = useState<RushdTreat | null>(null);
  const [showTreatForm, setShowTreatForm] = useState(false);
  const [showDeleteTreatDialog, setShowDeleteTreatDialog] = useState(false);
  const [treatToDelete, setTreatToDelete] = useState<RushdTreat | null>(null);
  const [deleteTreatLoading, setDeleteTreatLoading] = useState(false);

  // Free Item Rewards state
  const [freeItemRewards, setFreeItemRewards] = useState<FreeItemReward[]>([]);
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [rewardsError, setRewardsError] = useState<string | null>(null);
  const [showCreateRewardModal, setShowCreateRewardModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState<FreeItemReward | null>(null);
  const [showDeleteRewardDialog, setShowDeleteRewardDialog] = useState(false);

  // Sale Events modal state
  const [showCreateSaleEventModal, setShowCreateSaleEventModal] =
    useState(false);
  const [selectedSaleEvent, setSelectedSaleEvent] = useState<SaleEvent | null>(
    null
  );
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Discount Codes modal state
  const [showCreateDiscountCodeModal, setShowCreateDiscountCodeModal] =
    useState(false);
  const [showViewDiscountModal, setShowViewDiscountModal] = useState(false);
  const [showEditDiscountModal, setShowEditDiscountModal] = useState(false);
  const [showShareDiscountModal, setShowShareDiscountModal] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState<DiscountCode | null>(
    null
  );
  const [showDeleteDiscountModal, setShowDeleteDiscountModal] = useState(false);
  const [discountActionLoading, setDiscountActionLoading] = useState(false);

  // Email Campaign modal state
  const [showCreateEmailCampaignModal, setShowCreateEmailCampaignModal] = useState(false);
  const [showViewEmailCampaignModal, setShowViewEmailCampaignModal] = useState(false);
  const [showDeleteEmailCampaignModal, setShowDeleteEmailCampaignModal] = useState(false);
  const [selectedEmailCampaign, setSelectedEmailCampaign] = useState<EmailCampaign | null>(null);
  const [emailCampaignActionLoading, setEmailCampaignActionLoading] = useState(false);

  // Push Notifications state
  const [showCreatePushModal, setShowCreatePushModal] = useState(false);
  const [showViewPushModal, setShowViewPushModal] = useState(false);
  const [showDeletePushModal, setShowDeletePushModal] = useState(false);
  const [showBulkDeletePushModal, setShowBulkDeletePushModal] = useState(false);
  const [selectedPushNotification, setSelectedPushNotification] =
    useState<PushNotificationRecord | null>(null);
  const [createPushLoading, setCreatePushLoading] = useState(false);
  const [pushFormInitialValues, setPushFormInitialValues] =
    useState<PushNotificationFormValues | null>(null);
  const [selectedPushNotificationIds, setSelectedPushNotificationIds] =
    useState<string[]>([]);
  const [pushActionLoading, setPushActionLoading] = useState(false);
  const [bulkPushActionLoading, setBulkPushActionLoading] = useState(false);

  // Firebase hooks
  const {
    statistics: orderStatistics,
    percentageChanges: orderPercentageChanges,
    loading: orderStatisticsLoading,
    error: orderStatisticsError,
  } = useOrderStatistics();

  const {
    discountCodes,
    loading: discountCodesLoading,
    error: discountCodesError,
    refresh: refreshDiscountCodes,
    updateDiscountCode,
    addDiscountCode,
    deleteDiscountCode,
  } = useDiscounts();

  const {
    emailCampaigns,
    loading: emailCampaignsLoading,
    error: emailCampaignsError,
    refresh: refreshEmailCampaigns,
    createEmailCampaign,
    updateEmailCampaign,
    deleteEmailCampaign,
  } = useEmailCampaigns();

  const {
    pushNotifications,
    loading: pushNotificationsLoading,
    error: pushNotificationsError,
    refresh: refreshPushNotifications,
    sendBroadcastPush,
    deletePushNotification,
  } = usePushNotifications();

  const broadcastPushNotifications = useMemo(
    () =>
      pushNotifications.filter(
        (notification) =>
          !notification.targetedUserId && !notification.topic?.startsWith("user:")
      ),
    [pushNotifications]
  );

  const { refresh: refreshOrderStatistics } = useOrderStatistics();

  // Load Free Item Rewards
  const loadFreeItemRewards = async () => {
    setLoadingRewards(true);
    setRewardsError(null);
    try {
      const rewards = await getAllFreeItemRewards();
      setFreeItemRewards(rewards);
    } catch (error) {
      console.error('Error loading free item rewards:', error);
      setRewardsError('Failed to load free item rewards');
    } finally {
      setLoadingRewards(false);
    }
  };

  const loadRushdTreats = async () => {
    setRushdTreatsLoading(true);
    setRushdTreatsError(null);
    try {
      const treats = await getAllRushdTreats();
      setRushdTreats(treats);
    } catch (error) {
      console.error('Error loading Rush\'d treats:', error);
      setRushdTreatsError('Failed to load Rush\'d treats');
    } finally {
      setRushdTreatsLoading(false);
    }
  };

  const handleOpenTreatModal = (treat?: RushdTreat | null) => {
    setSelectedTreat(treat || null);
    setShowTreatForm(true);
  };

  const handleTreatSave = async (data: RushdTreatFormData) => {
    if (selectedTreat) {
      await updateRushdTreat(selectedTreat.id, {
        productId: data.productId,
        productName: data.productName,
        productImage: data.productImage || null,
        quantity: data.quantity,
        isActive: data.isActive,
      });
      toast.success("Rush'd treat updated");
    } else {
      await createRushdTreat({
        productId: data.productId,
        productName: data.productName,
        productImage: data.productImage || null,
        quantity: data.quantity,
        isActive: data.isActive,
      });
      toast.success("Rush'd treat created");
    }
    setSelectedTreat(null);
    setShowTreatForm(false);
    loadRushdTreats();
  };

  const handleToggleTreat = async (treat: RushdTreat) => {
    try {
      await toggleRushdTreat(treat.id, !treat.isActive);
      toast.success(`Rush'd treat ${treat.isActive ? 'disabled' : 'enabled'}`);
      loadRushdTreats();
    } catch (error) {
      console.error('Error toggling Rush\'d treat:', error);
      toast.error("Failed to update Rush'd treat");
    }
  };

  const handleConfirmDeleteTreat = async () => {
    if (!treatToDelete) return;
    try {
      setDeleteTreatLoading(true);
      await deleteRushdTreat(treatToDelete.id);
      toast.success("Rush'd treat deleted");
      setShowDeleteTreatDialog(false);
      setTreatToDelete(null);
      loadRushdTreats();
    } catch (error) {
      console.error('Error deleting Rush\'d treat:', error);
      toast.error("Failed to delete Rush'd treat");
    } finally {
      setDeleteTreatLoading(false);
    }
  };

  // Load rewards when tab changes to free_item_rewards
  useEffect(() => {
    if (activeTab === 'free_item_rewards') {
      loadFreeItemRewards();
    }
    if (activeTab === 'rushd_treats') {
      loadRushdTreats();
    }
  }, [activeTab]);

  // Handle refresh
  const handleRefresh = async () => {
    try {
      const refreshPromises = [
        refreshDiscountCodes(), 
        refreshOrderStatistics(),
        refreshEmailCampaigns(),
        refreshPushNotifications(),
      ];
      
      if (activeTab === 'free_item_rewards') {
        refreshPromises.push(loadFreeItemRewards());
      }
      if (activeTab === 'rushd_treats') {
        refreshPromises.push(loadRushdTreats());
      }
      
      await Promise.all(refreshPromises);
      toast.success("Marketing data refreshed successfully");
    } catch (error) {
      console.error("Error refreshing marketing data:", error);
      toast.error("Failed to refresh marketing data");
    }
  };

  // Sale Events handlers
  const handleCreateSaleEvent = () => {
    setShowCreateSaleEventModal(true);
  };

  const handleEditSaleEvent = (saleEvent: SaleEvent) => {
    setSelectedSaleEvent(saleEvent);
    // TODO: Add edit modal
    console.log("Edit sale event:", saleEvent);
  };

  const handleViewSaleEvent = (saleEvent: SaleEvent) => {
    // TODO: Add view modal/page
    console.log("View sale event:", saleEvent);
  };

  const handleSaleEventCreated = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Discount Code handlers
  const handleCreateDiscountCode = () => {
    setShowCreateDiscountCodeModal(true);
  };

  const handleDiscountCodeCreated = () => {
    // The real-time subscription will automatically update the data
  };

  // Email Campaign handlers
  const handleCreateEmailCampaign = () => {
    setShowCreateEmailCampaignModal(true);
  };

  const handleEmailCampaignCreated = () => {
    // Refresh campaigns data if needed
    toast.success("Email campaign processed successfully");
  };

  // Push Notification handlers
  const handleCreatePushNotification = () => {
    setPushFormInitialValues(null);
    setShowCreatePushModal(true);
  };

  const handleResendPushNotification = (
    notification: PushNotificationRecord,
  ) => {
    setPushFormInitialValues({
      title: notification.title,
      body: notification.body,
      route: notification.route ?? undefined,
      ctaLabel: notification.ctaLabel ?? "View Updates",
      imageUrl: notification.imageUrl ?? undefined,
      previewText: notification.previewText ?? undefined,
    });
    setShowCreatePushModal(true);
  };

  const handleSubmitPushNotification = async (
    values: PushNotificationFormValues
  ) => {
    setCreatePushLoading(true);
    try {
      await sendBroadcastPush(values);
      toast.success("Broadcast push notification sent to all mobile users");
      setShowCreatePushModal(false);
      setPushFormInitialValues(null);
    } catch (error) {
      console.error("Error sending broadcast push:", error);
      toast.error("Failed to send push notification");
    } finally {
      setCreatePushLoading(false);
    }
  };

  useEffect(() => {
    setSelectedPushNotificationIds((prev) =>
      prev.filter((id) =>
        broadcastPushNotifications.some((notification) => notification.id === id)
      )
    );
  }, [broadcastPushNotifications]);

  const selectedPushCount = selectedPushNotificationIds.length;
  const allPushSelected =
    broadcastPushNotifications.length > 0 &&
    selectedPushCount === broadcastPushNotifications.length;
  const somePushSelected =
    selectedPushCount > 0 &&
    selectedPushCount < broadcastPushNotifications.length;

  const handleTogglePushSelection = (id: string, isChecked: boolean) => {
    setSelectedPushNotificationIds((prev) => {
      if (isChecked) {
        if (prev.includes(id)) {
          return prev;
        }
        return [...prev, id];
      }
      return prev.filter((existing) => existing !== id);
    });
  };

  const handleToggleSelectAllPush = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedPushNotificationIds(broadcastPushNotifications.map((n) => n.id));
    } else {
      setSelectedPushNotificationIds([]);
    }
  };

  const handleBulkDeletePushNotifications = () => {
    setShowBulkDeletePushModal(true);
  };

  const handleConfirmBulkDeletePushNotifications = async () => {
    if (!selectedPushNotificationIds.length) {
      return;
    }
    setBulkPushActionLoading(true);
    try {
      await Promise.all(
        selectedPushNotificationIds.map((id) => deletePushNotification(id))
      );
      toast.success(
        `${selectedPushNotificationIds.length} broadcast${
          selectedPushNotificationIds.length === 1 ? '' : 's'
        } deleted`
      );
      setSelectedPushNotificationIds([]);
      setShowBulkDeletePushModal(false);
    } catch (error) {
      console.error('Error deleting push notifications:', error);
      toast.error('Failed to delete selected push notifications');
    } finally {
      setBulkPushActionLoading(false);
    }
  };

  const handleCloseCreatePushModal = () => {
    setShowCreatePushModal(false);
    setPushFormInitialValues(null);
  };

  const handleViewPushNotification = (notification: PushNotificationRecord) => {
    setSelectedPushNotification(notification);
    setShowViewPushModal(true);
  };

  const handleRequestDeletePushNotification = (
    notification: PushNotificationRecord
  ) => {
    setSelectedPushNotification(notification);
    setShowDeletePushModal(true);
  };

  const handleConfirmDeletePushNotification = async () => {
    if (!selectedPushNotification) return;
    setPushActionLoading(true);
    try {
      await deletePushNotification(selectedPushNotification.id);
      toast.success("Push notification deleted");
      setShowDeletePushModal(false);
      setSelectedPushNotification(null);
      setSelectedPushNotificationIds((prev) =>
        prev.filter((id) => id !== selectedPushNotification.id)
      );
    } catch (error) {
      console.error("Error deleting push notification:", error);
      toast.error("Failed to delete push notification");
    } finally {
      setPushActionLoading(false);
    }
  };

  // Free Item Rewards handlers
  const handleCreateFreeItemReward = () => {
    setShowCreateRewardModal(true);
  };

  const handleRewardCreated = () => {
    loadFreeItemRewards();
    setShowCreateRewardModal(false);
  };

  const handleToggleReward = async (reward: FreeItemReward) => {
    try {
      await toggleFreeItemReward(reward.id, !reward.isActive);
      toast.success(`Reward ${reward.isActive ? 'disabled' : 'enabled'} successfully`);
      loadFreeItemRewards();
    } catch (error) {
      console.error('Error toggling reward:', error);
      toast.error('Failed to update reward');
    }
  };

  const handleDeleteReward = (reward: FreeItemReward) => {
    setSelectedReward(reward);
    setShowDeleteRewardDialog(true);
  };

  const handleConfirmDeleteReward = async () => {
    if (!selectedReward) return;

    try {
      await deleteFreeItemReward(selectedReward.id);
      toast.success('Reward deleted successfully');
      loadFreeItemRewards();
      setShowDeleteRewardDialog(false);
      setSelectedReward(null);
    } catch (error) {
      console.error('Error deleting reward:', error);
      toast.error('Failed to delete reward');
    }
  };

  const handleViewEmailCampaign = (campaign: EmailCampaign) => {
    setSelectedEmailCampaign(campaign);
    setShowViewEmailCampaignModal(true);
  };



  const handleDeleteEmailCampaign = (campaign: EmailCampaign) => {
    setSelectedEmailCampaign(campaign);
    setShowDeleteEmailCampaignModal(true);
  };

  const handleConfirmDeleteEmailCampaign = async () => {
    if (!selectedEmailCampaign) return;

    try {
      setEmailCampaignActionLoading(true);
      await deleteEmailCampaign(selectedEmailCampaign.id);
      toast.success("Email campaign deleted successfully");
      setShowDeleteEmailCampaignModal(false);
      setSelectedEmailCampaign(null);
    } catch (error) {
      console.error("Error deleting email campaign:", error);
      toast.error("Failed to delete email campaign");
    } finally {
      setEmailCampaignActionLoading(false);
    }
  };

  // Discount Code action handlers
  const handleViewDiscount = (discount: DiscountCode) => {
    setSelectedDiscount(discount);
    setShowViewDiscountModal(true);
  };

  const handleEditDiscount = (discount: DiscountCode) => {
    setSelectedDiscount(discount);
    setShowEditDiscountModal(true);
  };

  const handleShareDiscount = (discount: DiscountCode) => {
    setSelectedDiscount(discount);
    setShowShareDiscountModal(true);
  };

  const handleDeleteDiscount = (discount: DiscountCode) => {
    setSelectedDiscount(discount);
    setShowDeleteDiscountModal(true);
  };

  const handleConfirmDeleteDiscount = async () => {
    if (!selectedDiscount) return;

    setDiscountActionLoading(true);
    try {
      await deleteDiscountCode(selectedDiscount.id);
      toast.success("Discount code deleted successfully");
      setShowDeleteDiscountModal(false);
      setSelectedDiscount(null);
    } catch (error) {
      console.error("Error deleting discount code:", error);
      toast.error("Failed to delete discount code");
    } finally {
      setDiscountActionLoading(false);
    }
  };

  const handleUpdateDiscount = async (
    id: string,
    updates: Partial<Omit<DiscountCode, "id" | "createdAt" | "updatedAt">>
  ) => {
    try {
      await updateDiscountCode(id, updates);
      // The real-time subscription will automatically update the data
    } catch (error) {
      console.error("Error updating discount code:", error);
      throw error;
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "active":
      case "sent":
        return {
          color: "bg-green-100 text-green-800",
          label: status.charAt(0).toUpperCase() + status.slice(1),
        };
      case "inactive":
      case "expired":
        return {
          color: "bg-gray-100 text-gray-800",
          label: status.charAt(0).toUpperCase() + status.slice(1),
        };
      case "scheduled":
        return { color: "bg-blue-100 text-blue-800", label: "Scheduled" };
      case "draft":
        return { color: "bg-yellow-100 text-yellow-800", label: "Draft" };
      default:
        return { color: "bg-gray-100 text-gray-800", label: "Unknown" };
    }
  };

  const getDiscountTypeConfig = (type: string) => {
    switch (type) {
      case "percentage":
        return {
          color: "bg-jamaica-yellow/20 text-jamaica-yellow",
          label: "Percentage",
          icon: Percent,
        };
      case "fixed":
        return {
          color: "bg-green-100 text-green-800",
          label: "Fixed Amount",
          icon: DollarSign,
        };
      case "free_shipping":
        return {
          color: "bg-blue-100 text-blue-800",
          label: "Free Shipping",
          icon: Gift,
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800",
          label: "Unknown",
          icon: Tag,
        };
    }
  };

  // Calculate stats
  const activeDiscounts = discountCodes.filter(
    (d) => d.status === "active"
  ).length;
  const totalDiscountUses = discountCodes.reduce(
    (sum, d) => sum + d.currentUses,
    0
  );
  const sentCampaigns = emailCampaigns.filter(
    (e) => e.status === "sent"
  ).length;
  const totalEmailRevenue = 0; // Revenue tracking removed per requirements
  const activeSales = dummySaleEvents.filter(
    (s) => s.status === "active"
  ).length;
  const totalSaleRevenue = dummySaleEvents.reduce(
    (sum, s) => sum + s.actualRevenue,
    0
  );

  // Get real revenue data from Firebase
  const totalRevenue = orderStatistics?.totalRevenue ?? 0;
  const revenueChange = orderPercentageChanges?.totalRevenue ?? 0;

  const renderFreeItemRewardsTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-jamaica-green" />
            Free Item Rewards ({freeItemRewards.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingRewards ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
              <span className="ml-2 text-gray-600">
                Loading free item rewards...
              </span>
            </div>
          ) : rewardsError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error Loading Free Item Rewards
              </h3>
              <p className="text-gray-600 mb-4">{rewardsError}</p>
              <Button onClick={loadFreeItemRewards} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : freeItemRewards.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Gift className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Free Item Rewards Found
              </h3>
              <p className="text-gray-600 mb-4">
                No free item rewards have been created yet.
              </p>
              <Button
                className="flex items-center gap-2"
                onClick={handleCreateFreeItemReward}
              >
                <Plus className="w-4 h-4" />
                Create First Free Item Reward
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Product</TableHead>
                    <TableHead>Threshold</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {freeItemRewards.map((reward, index) => (
                    <TableRow
                      key={reward.id}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        opacity: 0,
                        transform: "translateX(-20px)",
                        animation: `fadeInSlide 0.3s ease-out ${
                          index * 0.05
                        }s forwards`,
                      }}
                    >
                      <TableCell>
                        {reward.productId ? (
                          <div className="flex items-center gap-3">
                            <img
                              src={reward.productImage}
                              alt={reward.productName}
                              className="w-10 h-10 rounded-md object-cover"
                            />
                            <div>
                              <p className="font-medium text-sm">{reward.productName}</p>
                              <p className="text-xs text-gray-500">ID: {reward.productId}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center">
                              <Target className="h-5 w-5 text-gray-400" />
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-700">
                                {reward.milestoneLabel || "Milestone Only"}
                              </p>
                              <p className="text-xs text-gray-500">
                                {reward.milestoneLabel ? "Custom milestone" : "No product linked"}
                              </p>
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3 text-gray-400" />
                          <span className="font-medium">{reward.threshold.toFixed(2)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            reward.isActive
                              ? "bg-green-100 text-green-800 border-green-200"
                              : "bg-gray-100 text-gray-800 border-gray-200"
                          }
                        >
                          {reward.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {formatDate(reward.createdAt)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleToggleReward(reward)}
                            >
                              {reward.isActive ? (
                                <>
                                  <Lock className="h-4 w-4 mr-2" />
                                  Disable
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Enable
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleDeleteReward(reward)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Reward
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderRushdTreatsTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-600" />
              Rush'd Treats ({rushdTreats.length})
            </span>
              <Button
                size="sm"
                onClick={() => handleOpenTreatModal(null)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Treat
              </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {rushdTreatsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
              <span className="ml-2 text-gray-600">Loading Rush'd treats...</span>
            </div>
          ) : rushdTreatsError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Rush'd Treats</h3>
              <p className="text-gray-600 mb-4">{rushdTreatsError}</p>
              <Button onClick={loadRushdTreats} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : rushdTreats.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Crown className="h-12 w-12 text-amber-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Rush'd Treats Found</h3>
              <p className="text-gray-600 mb-4">Configure free treats for active Rush'd members.</p>
              <Button className="flex items-center gap-2" onClick={() => { setSelectedTreat(null); setShowTreatForm(true); }}>
                <Plus className="w-4 h-4" />
                Add Rush'd Treat
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Product</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rushdTreats.map((treat, index) => (
                    <TableRow
                      key={treat.id}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        opacity: 0,
                        transform: "translateX(-20px)",
                        animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s forwards`,
                      }}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {treat.productImage ? (
                            <img src={treat.productImage} alt={treat.productName} className="w-10 h-10 rounded-md object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-md bg-amber-50 flex items-center justify-center">
                              <Crown className="h-5 w-5 text-amber-500" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-sm">{treat.productName}</p>
                            <p className="text-xs text-gray-500">ID: {treat.productId}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-gray-900">{treat.quantity}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={treat.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                          {treat.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-600">
                          {formatDate(treat.createdAt)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenTreatModal(treat)}>
                              Edit Treat
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleTreat(treat)}>
                              {treat.isActive ? "Disable Treat" : "Enable Treat"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600 focus:text-red-600"
                              onClick={() => {
                                setTreatToDelete(treat);
                                setShowDeleteTreatDialog(true);
                              }}
                            >
                              Delete Treat
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderDiscountCodesTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-jamaica-green" />
            Discount Codes ({discountCodes.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {discountCodesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
              <span className="ml-2 text-gray-600">
                Loading discount codes...
              </span>
            </div>
          ) : discountCodesError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error Loading Discount Codes
              </h3>
              <p className="text-gray-600 mb-4">{discountCodesError}</p>
              <Button onClick={refreshDiscountCodes} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : discountCodes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Tag className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Discount Codes Found
              </h3>
              <p className="text-gray-600 mb-4">
                No discount codes have been created yet.
              </p>
              <Button
                className="flex items-center gap-2"
                onClick={handleCreateDiscountCode}
              >
                <Plus className="w-4 h-4" />
                Create First Discount Code
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Code</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Affiliate</TableHead>
                    <TableHead>Valid Period</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {discountCodes.map((discount, index) => {
                    const statusConfig = getStatusConfig(discount.status);
                    const typeConfig = getDiscountTypeConfig(discount.type);
                    const TypeIcon = typeConfig.icon;
                    const usagePercentage =
                      (discount.currentUses / discount.maxUses) * 100;

                    return (
                      <TableRow
                        key={discount.id}
                        className="hover:bg-gray-50 transition-colors"
                        style={{
                          opacity: 0,
                          transform: "translateX(-20px)",
                          animation: `fadeInSlide 0.3s ease-out ${
                            index * 0.05
                          }s forwards`,
                        }}
                      >
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <code className="font-mono font-bold text-jamaica-green bg-jamaica-green/10 px-2 py-1 rounded">
                                {discount.code}
                              </code>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                            {discount.membersOnly && (
                              <Badge className="bg-amber-100 text-amber-800 border-amber-200 flex items-center gap-1 w-fit">
                                <Crown className="w-3 h-3" />
                                Rush'd Only
                              </Badge>
                            )}
                            <p className="text-sm text-gray-500 line-clamp-1">
                              {discount.description}
                            </p>
                            <p className="text-xs text-gray-400">
                              Min order:{" "}
                              {formatCurrency(discount.minOrderAmount)}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={typeConfig.color}>
                            <TypeIcon className="w-3 h-3 mr-1" />
                            {typeConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {discount.type === "percentage" ? (
                              <p className="font-semibold text-gray-900">
                                {discount.value}%
                              </p>
                            ) : discount.type === "fixed" ? (
                              <p className="font-semibold text-gray-900">
                                {formatCurrency(discount.value)}
                              </p>
                            ) : (
                              <p className="font-semibold text-gray-900">
                                Free Shipping
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-medium text-gray-900">
                              {discount.currentUses} / {discount.maxUses}
                            </p>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-jamaica-green h-2 rounded-full transition-all duration-300"
                                style={{
                                  width: `${Math.min(usagePercentage, 100)}%`,
                                }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-500">
                              {usagePercentage.toFixed(1)}% used
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {discount.affiliateId ? (
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  Linked
                                </p>
                                <p className="text-xs text-gray-500">
                                  ID: {discount.affiliateId.slice(-8)}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-gray-300" />
                              <span className="text-sm text-gray-500">No affiliate</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-gray-400" />
                              <p className="text-sm text-gray-600">
                                {formatDate(discount.startDate)}
                              </p>
                            </div>
                            <p className="text-xs text-gray-500">
                              Ends: {formatDate(discount.endDate)}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleViewDiscount(discount)}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleEditDiscount(discount)}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Code
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleShareDiscount(discount)}
                              >
                                <Share2 className="w-4 h-4 mr-2" />
                                Share Code
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => handleDeleteDiscount(discount)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Code
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderEmailCampaignsTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-jamaica-green" />
            Email Campaigns ({emailCampaigns.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {emailCampaignsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
              <span className="ml-2 text-gray-600">
                Loading email campaigns...
              </span>
            </div>
          ) : emailCampaignsError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error Loading Email Campaigns
              </h3>
              <p className="text-gray-600 mb-4">{emailCampaignsError}</p>
              <Button onClick={refreshEmailCampaigns} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : emailCampaigns.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Mail className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Email Campaigns Found
              </h3>
              <p className="text-gray-600 mb-4">
                No email campaigns have been created yet.
              </p>
              <Button
                className="flex items-center gap-2"
                onClick={handleCreateEmailCampaign}
              >
                <Plus className="w-4 h-4" />
                Create First Email Campaign
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Campaign</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emailCampaigns.map((campaign, index) => {
                  const statusConfig = getStatusConfig(campaign.status);

                  return (
                    <TableRow
                      key={campaign.id}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        opacity: 0,
                        transform: "translateX(-20px)",
                        animation: `fadeInSlide 0.3s ease-out ${
                          index * 0.05
                        }s forwards`,
                      }}
                    >
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium text-gray-900">
                            {campaign.subject}
                          </p>
                          <p className="text-sm text-gray-500 line-clamp-2">
                            {campaign.content?.substring(0, 100)}...
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {campaign.type.charAt(0).toUpperCase() +
                            campaign.type.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-gray-400" />
                          <p className="font-medium text-gray-900">
                            {campaign.recipients.toLocaleString()}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            <p className="text-sm text-gray-600">
                              {formatDate(campaign.sentDate)}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewEmailCampaign(campaign)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Campaign
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleDeleteEmailCampaign(campaign)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Campaign
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderSaleEventsTab = () => (
    <SaleEventsList
      onCreateSaleEvent={handleCreateSaleEvent}
      onEditSaleEvent={handleEditSaleEvent}
      onViewSaleEvent={handleViewSaleEvent}
      refreshTrigger={refreshTrigger}
    />
  );

  const renderPushNotificationsTab = () => (
    <Card className="admin-card">
      <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-gray-900">
            <Bell className="w-4 h-4 text-jamaica-green" />
            Broadcast History
          </CardTitle>
          <p className="text-sm text-gray-500">
            Broadcast pushes sent via the JamPack’d admin portal (user-specific sends are hidden).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {selectedPushCount > 0 && (
            <Button
              variant="destructive"
              size="sm"
              disabled={bulkPushActionLoading}
              className="flex items-center gap-2"
              onClick={handleBulkDeletePushNotifications}
            >
              {bulkPushActionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Delete Selected ({selectedPushCount})
            </Button>
          )}
          <Button
            className="bg-jamaica-green text-white hover:bg-jamaica-green/90 flex items-center gap-2"
            onClick={handleCreatePushNotification}
          >
            <Bell className="w-4 h-4" />
            Create Broadcast
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {pushNotificationsLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500 gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading push notifications...
          </div>
        ) : pushNotificationsError ? (
          <div className="flex items-center justify-between bg-red-50 border border-red-100 rounded-xl p-4">
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              {pushNotificationsError}
            </div>
            <Button variant="outline" size="sm" onClick={refreshPushNotifications}>
              Retry
            </Button>
          </div>
        ) : broadcastPushNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Bell className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No broadcast push notifications yet
            </h3>
            <p className="text-gray-600 mb-4 max-w-md">
              Broadcast announcements, flash sales, or order tips directly to every mobile install.
            </p>
            <Button className="flex items-center gap-2" onClick={handleCreatePushNotification}>
              <Plus className="w-4 h-4" />
              Create First Broadcast
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allPushSelected ? true : somePushSelected ? "indeterminate" : false}
                      onCheckedChange={(checked) => handleToggleSelectAllPush(checked === true)}
                      aria-label="Select all broadcasts"
                    />
                  </TableHead>
                  <TableHead>Notification</TableHead>
                  <TableHead>Audience</TableHead>
                  <TableHead>Devices</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {broadcastPushNotifications.map((notification, index) => (
                  <TableRow
                    key={notification.id}
                    className="hover:bg-gray-50 transition-colors"
                    style={{
                      opacity: 0,
                      transform: "translateX(-20px)",
                      animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s forwards`,
                    }}
                  >
                    <TableCell className="w-12">
                      <Checkbox
                        checked={selectedPushNotificationIds.includes(notification.id)}
                        onCheckedChange={(checked) =>
                          handleTogglePushSelection(notification.id, checked === true)
                        }
                        aria-label="Select broadcast"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900">
                            {notification.title}
                          </p>
                          {notification.previewText && (
                            <Badge className="bg-purple-50 text-purple-700 border border-purple-100">
                              {notification.previewText}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {notification.body}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-jamaica-green/10 text-jamaica-green border border-jamaica-green/20 capitalize">
                        {notification.topic === "all" ? "All installs" : notification.topic}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-800">
                        {typeof notification.targetedTokens === "number"
                          ? notification.targetedTokens.toLocaleString()
                          : "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {notification.route ? (
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Link2 className="w-4 h-4 text-gray-400" />
                          {notification.route}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">Default (home)</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(notification.sentAt)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewPushNotification(notification)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleResendPushNotification(notification)}>
                            <RefreshCcw className="w-4 h-4 mr-2" />
                            Resend Broadcast
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleRequestDeletePushNotification(notification)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Record
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <>
      <style>{`
        @keyframes fadeInSlide {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
      <div className="space-y-6">
        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex justify-between items-center"
        >
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={
                discountCodesLoading ||
                orderStatisticsLoading ||
                emailCampaignsLoading ||
                pushNotificationsLoading
              }
              className="flex items-center gap-2"
            >
              {discountCodesLoading ||
              orderStatisticsLoading ||
              emailCampaignsLoading ||
              pushNotificationsLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Refresh
            </Button>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-jamaica-green hover:bg-jamaica-green/90 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleCreateDiscountCode}>
                <Tag className="w-4 h-4 mr-2" />
                Create Discount Code
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCreateEmailCampaign}>
                <Mail className="w-4 h-4 mr-2" />
                Create Email Campaign
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCreatePushNotification}>
                <Bell className="w-4 h-4 mr-2" />
                Create Broadcast Mobile Push
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCreateFreeItemReward}>
                <Gift className="w-4 h-4 mr-2" />
                Create Free Item Reward
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleOpenTreatModal(null)}>
                <Crown className="w-4 h-4 mr-2" />
                Create Rush'd Treat
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled
                className="opacity-50 cursor-not-allowed"
              >
                <Megaphone className="w-4 h-4 mr-2" />
                Create Sale Event
                <Lock className="w-3 h-3 ml-auto text-gray-400" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Active Discounts
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {activeDiscounts}
                  </p>
                </div>
                <div className="h-12 w-12 bg-jamaica-green/10 rounded-lg flex items-center justify-center">
                  <Tag className="h-6 w-6 text-jamaica-green" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-green-600">
                  {totalDiscountUses} total uses
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Email Campaigns Card */}
          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Email Campaigns
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {emailCampaigns.length}
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-green-600">
                  {emailCampaigns.filter(c => c.status === 'sent').length} sent campaigns
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Disabled Active Sales Card */}
          <Card className="admin-card opacity-60 bg-gray-50 border-dashed">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Sale Events
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <Lock className="h-4 w-4 text-gray-400" />
                    <p className="text-lg font-bold text-gray-400">
                      Coming Soon
                    </p>
                  </div>
                </div>
                <div className="h-12 w-12 bg-gray-200 rounded-lg flex items-center justify-center">
                  <Megaphone className="h-6 w-6 text-gray-400" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <span className="text-gray-400">Feature in development</span>
              </div>
            </CardContent>
          </Card>

          {/* Connected Revenue Card */}
          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Revenue
                  </p>
                  {orderStatisticsLoading ? (
                    <div className="flex items-center gap-2 mt-1">
                      <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                      <span className="text-sm text-gray-500">Loading...</span>
                    </div>
                  ) : orderStatisticsError ? (
                    <p className="text-2xl font-bold text-red-600">Error</p>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(totalRevenue)}
                    </p>
                  )}
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                {orderStatisticsLoading ? (
                  <span className="text-gray-500">Loading trend...</span>
                ) : orderStatisticsError ? (
                  <span className="text-red-500">Error loading trend</span>
                ) : revenueChange !== 0 ? (
                  <>
                    {revenueChange > 0 ? (
                      <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
                    ) : (
                      <ArrowDownRight className="h-4 w-4 text-red-500 mr-1" />
                    )}
                    <span
                      className={
                        revenueChange > 0 ? "text-green-600" : "text-red-600"
                      }
                    >
                      {formatPercentage(Math.abs(revenueChange))} vs last month
                    </span>
                  </>
                ) : (
                  <span className="text-gray-500">No historical data</span>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Marketing Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-white rounded-xl shadow-md border border-gray-100"
        >
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-6">
              {[
                {
                  id: "discount_codes",
                  label: "Discount Codes",
                  icon: Tag,
                  disabled: false,
                },
                {
                  id: "email_campaigns",
                  label: "Email Campaigns",
                  icon: Mail,
                  disabled: false,
                },
                {
                  id: "push_notifications",
                  label: "Mobile Push",
                  icon: Bell,
                  disabled: false,
                },
                {
                  id: "free_item_rewards",
                  label: "Free Item Rewards",
                  icon: Gift,
                  disabled: false,
                },
                {
                  id: "rushd_treats",
                  label: "Rush'd Treats",
                  icon: Crown,
                  disabled: false,
                },
                {
                  id: "sale_events",
                  label: "Sale Events",
                  icon: Megaphone,
                  disabled: true,
                },
              ].map(({ id, label, icon: Icon, disabled }) => (
                <button
                  key={id}
                  onClick={() => !disabled && setActiveTab(id)}
                  disabled={disabled}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors relative ${
                    activeTab === id && !disabled
                      ? "border-jamaica-green text-jamaica-green"
                      : disabled
                      ? "border-transparent text-gray-400 cursor-not-allowed opacity-60"
                      : "border-transparent text-gray-500 hover:text-gray-700 cursor-pointer"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "discount_codes" && renderDiscountCodesTab()}
            {activeTab === "email_campaigns" && renderEmailCampaignsTab()}
            {activeTab === "push_notifications" && renderPushNotificationsTab()}
            {activeTab === "free_item_rewards" && renderFreeItemRewardsTab()}
            {activeTab === "rushd_treats" && renderRushdTreatsTab()}
            {activeTab === "sale_events" && (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Sale Events Coming Soon
                </h3>
                <p className="text-gray-600">
                  This feature is currently in development and will be available
                  soon.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Create Sale Event Modal - Commented out for now */}
      {/* <CreateSaleEventModal
        isOpen={showCreateSaleEventModal}
        onClose={() => setShowCreateSaleEventModal(false)}
        onSuccess={handleSaleEventCreated}
        currentUserId="admin-user-id" // TODO: Get from auth context
      /> */}

      {/* Create Discount Code Modal */}
      <CreateDiscountCodeModal
        isOpen={showCreateDiscountCodeModal}
        onClose={() => setShowCreateDiscountCodeModal(false)}
        onSubmit={addDiscountCode}
      />

      {/* View Discount Code Modal */}
      <ViewDiscountCodeModal
        isOpen={showViewDiscountModal}
        onClose={() => setShowViewDiscountModal(false)}
        discount={selectedDiscount}
        onEdit={handleEditDiscount}
        onShare={handleShareDiscount}
        onDelete={handleDeleteDiscount}
      />

      {/* Edit Discount Code Modal */}
      <EditDiscountCodeModal
        isOpen={showEditDiscountModal}
        onClose={() => {
          setShowEditDiscountModal(false);
          setSelectedDiscount(null);
        }}
        discount={selectedDiscount}
        onUpdate={refreshDiscountCodes}
      />

      {/* Share Discount Code Modal */}
      <ShareDiscountCodeModal
        isOpen={showShareDiscountModal}
        onClose={() => setShowShareDiscountModal(false)}
        discount={selectedDiscount}
      />

      {/* Delete Discount Code Confirmation */}
      <DeleteConfirmDialog
        isOpen={showDeleteDiscountModal}
        onClose={() => setShowDeleteDiscountModal(false)}
        onConfirm={handleConfirmDeleteDiscount}
        title="Delete Discount Code"
        description={`Are you sure you want to delete the discount code "${selectedDiscount?.code}"? This action cannot be undone.`}
        itemName={selectedDiscount?.code}
        loading={discountActionLoading}
      />

      {/* Create Email Campaign Modal */}
      <CreateEmailCampaignModal
        isOpen={showCreateEmailCampaignModal}
        onClose={() => setShowCreateEmailCampaignModal(false)}
        onCampaignCreated={handleEmailCampaignCreated}
      />

      {/* View Email Campaign Modal */}
      <ViewEmailCampaignModal
        isOpen={showViewEmailCampaignModal}
        onClose={() => {
          setShowViewEmailCampaignModal(false);
          setSelectedEmailCampaign(null);
        }}
        campaign={selectedEmailCampaign}
        onDelete={handleDeleteEmailCampaign}
      />

      {/* Delete Email Campaign Confirmation */}
      <DeleteConfirmDialog
        isOpen={showDeleteEmailCampaignModal}
        onClose={() => {
          setShowDeleteEmailCampaignModal(false);
          setSelectedEmailCampaign(null);
        }}
        onConfirm={handleConfirmDeleteEmailCampaign}
        title="Delete Email Campaign"
        description={`Are you sure you want to delete the email campaign "${selectedEmailCampaign?.subject}"? This action cannot be undone.`}
        itemName={selectedEmailCampaign?.subject}
        loading={emailCampaignActionLoading}
      />

      {/* Push Notification Modals */}
      <CreatePushNotificationModal
        isOpen={showCreatePushModal}
        onClose={handleCloseCreatePushModal}
        onSubmit={handleSubmitPushNotification}
        loading={createPushLoading}
        initialValues={pushFormInitialValues ?? undefined}
        mode={pushFormInitialValues ? "resend" : "create"}
      />

      <ViewPushNotificationModal
        isOpen={showViewPushModal}
        onClose={() => {
          setShowViewPushModal(false);
          setSelectedPushNotification(null);
        }}
        notification={selectedPushNotification}
        onDelete={handleRequestDeletePushNotification}
      />

      <DeleteConfirmDialog
        isOpen={showDeletePushModal}
        onClose={() => {
          setShowDeletePushModal(false);
          setSelectedPushNotification(null);
        }}
        onConfirm={handleConfirmDeletePushNotification}
        title="Delete Push Notification"
        description="This removes the broadcast record from history. Delivered notifications cannot be recalled."
        itemName={selectedPushNotification?.title}
        loading={pushActionLoading}
      />

      <DeleteConfirmDialog
        isOpen={showBulkDeletePushModal}
        onClose={() => setShowBulkDeletePushModal(false)}
        onConfirm={handleConfirmBulkDeletePushNotifications}
        title="Delete Selected Broadcasts"
        description="This removes the selected broadcast records from history. Delivered notifications cannot be recalled."
        itemName={`${selectedPushCount} broadcast${selectedPushCount === 1 ? '' : 's'}`}
        loading={bulkPushActionLoading}
      />

      {/* Create Free Item Reward Modal */}
      <CreateFreeItemRewardModal
        isOpen={showCreateRewardModal}
        onClose={() => setShowCreateRewardModal(false)}
        onRewardCreated={handleRewardCreated}
      />

      <CreateRushdTreatModal
        isOpen={showTreatForm}
        onClose={() => {
          setShowTreatForm(false);
          setSelectedTreat(null);
        }}
        initialTreat={selectedTreat ? {
          productId: selectedTreat.productId,
          productName: selectedTreat.productName,
          productImage: selectedTreat.productImage || null,
          quantity: selectedTreat.quantity,
          isActive: selectedTreat.isActive,
        } : null}
        onSave={handleTreatSave}
      />

      <DeleteConfirmDialog
        isOpen={showDeleteTreatDialog}
        onClose={() => {
          setShowDeleteTreatDialog(false);
          setTreatToDelete(null);
        }}
        onConfirm={handleConfirmDeleteTreat}
        title="Delete Rush'd Treat"
        description="This will remove the treat and it will no longer be auto-added for Rush'd members."
        itemName={treatToDelete?.productName}
        loading={deleteTreatLoading}
      />

      {/* Delete Free Item Reward Dialog */}
      <DeleteConfirmDialog
        isOpen={showDeleteRewardDialog}
        onClose={() => {
          setShowDeleteRewardDialog(false);
          setSelectedReward(null);
        }}
        onConfirm={handleConfirmDeleteReward}
        title="Delete Free Item Reward"
        description={`Are you sure you want to delete the reward for "${selectedReward?.productName}"? This action cannot be undone.`}
        itemName={selectedReward?.productName}
        loading={false}
      />
    </>
  );
}
